import type { ProblemDetails } from '@kanbix/shared';
import { useAuth } from './auth';

export const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem: ProblemDetails & Record<string, unknown>,
  ) {
    super(problem.title || `Request failed (${status})`);
  }
  get code() {
    return this.problem.code as string | undefined;
  }
}

/** Problem details from an error body; plain-text bodies (proxy errors, rate limits) become the title. */
function problemFrom(text: string, res: Response): ProblemDetails & Record<string, unknown> {
  try {
    if (text) return JSON.parse(text);
  } catch {
    /* not JSON */
  }
  const title = res.status === 429 ? 'Too many requests — please wait a moment and try again' : text.slice(0, 200) || res.statusText || `Request failed (${res.status})`;
  return { type: 'about:blank', title, status: res.status } as ProblemDetails & Record<string, unknown>;
}

let refreshing: Promise<boolean> | null = null;

/** Rotates the refresh cookie and stores the new access token (§13). */
export async function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, { method: 'POST', credentials: 'include', headers: { 'x-kanbix-csrf': '1' } });
      if (!res.ok) return false;
      const body = await res.json();
      useAuth.getState().setSession(body.accessToken, body.user, body.expiresIn);
      return true;
    } catch {
      return false;
    } finally {
      setTimeout(() => (refreshing = null), 0);
    }
  })();
  return refreshing;
}

export async function api<T = unknown>(method: string, path: string, body?: unknown, opts: { signal?: AbortSignal; retry?: boolean } = {}): Promise<T> {
  const token = useAuth.getState().token;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    signal: opts.signal,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401 && opts.retry !== false && path !== '/auth/refresh') {
    if (await refreshSession()) return api<T>(method, path, body, { ...opts, retry: false });
    useAuth.getState().clear();
  }
  if (res.status === 204) return null as T;
  const text = await res.text();
  // Empty 200 bodies (e.g. "no running timer") become null: query functions must never resolve undefined.
  if (!res.ok) throw new ApiError(res.status, problemFrom(text, res));
  const data = text ? JSON.parse(text) : null;
  return data as T;
}

/** Authenticated file download (CSV exports): fetch with the bearer token, then save via an object URL. */
export async function download(path: string, fallbackName: string) {
  const token = useAuth.getState().token;
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include', headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    throw new ApiError(res.status, problemFrom(await res.text(), res));
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const http = {
  get: <T>(p: string, signal?: AbortSignal) => api<T>('GET', p, undefined, { signal }),
  post: <T>(p: string, body?: unknown) => api<T>('POST', p, body ?? {}),
  patch: <T>(p: string, body?: unknown) => api<T>('PATCH', p, body ?? {}),
  put: <T>(p: string, body?: unknown) => api<T>('PUT', p, body ?? {}),
  del: <T>(p: string) => api<T>('DELETE', p),
};

export const qs = (params: Record<string, string | number | boolean | undefined | null>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') s.set(k, String(v));
  const out = s.toString();
  return out ? `?${out}` : '';
};

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const details = (err.problem.errors as { path: string; message: string }[] | undefined)?.map((e) => `${e.path}: ${e.message}`).join('; ');
    return details ? `${err.message} — ${details}` : err.message;
  }
  return (err as Error)?.message ?? 'Something went wrong';
}

/** Server-Sent Events over POST (assistant streaming). */
export async function postStream(path: string, body: unknown, onEvent: (type: string, data: Record<string, unknown>) => void, signal?: AbortSignal) {
  const doFetch = () =>
    fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      signal,
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', Authorization: `Bearer ${useAuth.getState().token}` },
      body: JSON.stringify(body),
    });
  let res = await doFetch();
  if (res.status === 401 && (await refreshSession())) res = await doFetch();
  if (!res.ok || !res.body) {
    throw new ApiError(res.status, problemFrom(await res.text(), res));
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const chunks = buf.split('\n\n');
    buf = chunks.pop() ?? '';
    for (const chunk of chunks) {
      const type = /^event: (.*)$/m.exec(chunk)?.[1];
      const data = /^data: (.*)$/m.exec(chunk)?.[1];
      if (type && data) onEvent(type, JSON.parse(data));
    }
  }
}
